import _ from "lodash";
import { useCallback, useEffect, useReducer, useMemo } from "react";
import update from "immutability-helper";

import client from "lib/apolloClient/client";
import { useDispatch } from "Hooks/useDispatch";
import { isIdEqual } from "Utils/idHelpers";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { cleanUpFailedMessages } from "MessagingV2/modules/MessagingModule";
import { getOrganizationId } from "modules/CommonSelectors";

import {
  getChatConversationsFromCache,
  writeChatConversationsToCache,
} from "../MessagingGraphqlHelper";
import { getChatConversationsQuery, getMessageQuery } from "../MessagingQuery";
import {
  MESSAGE_TYPE,
  PAGINATION_LIMITS,
  getChatSessionKey,
  getIsLocalMessage,
  isMessagingRevempEnabled,
} from "../Utils";

export const CURSOR_MESSAGE_TYPES = {
  LAST_READ_MESSAGE: "LAST_READ_MESSAGE",
  JUMP_TO_MESSAGE: "JUMP_TO_MESSAGE",
};

const SESSION_SET = new Set();

const preparePayload = ({
  beforeCursorData,
  // following can be undefined in case of null cursor
  afterCursorData,
  cursorMessageData,
}) => {
  const beforeEdges = _.get(beforeCursorData, "node.messagesV2.edges", []);
  const afterEdges = _.get(afterCursorData, "node.messagesV2.edges", []);

  // Any of the before or after edges will have the total count and same node
  const totalCount = _.get(beforeCursorData, "node.messagesV2.totalCount", 0);
  const node = _.get(beforeCursorData, "node", {});

  const lastReadMessageEdge = cursorMessageData
    ? {
        __typename: "MessageV2Edge",
        cursor: cursorMessageData.node?.cursor,
        node: cursorMessageData.node,
      }
    : undefined;

  const descOrderAfterEdges = _.reverse([...afterEdges]);

  return {
    ...node,
    messagesV2: {
      __typename: "MessageV2Connection",
      totalCount,
      edges: lastReadMessageEdge
        ? [...descOrderAfterEdges, lastReadMessageEdge, ...beforeEdges]
        : [...descOrderAfterEdges, ...beforeEdges],
      pageInfo: {
        hasPreviousPage: _.get(
          beforeCursorData,
          "node.messagesV2.pageInfo.hasNextPage", // TODO: change to hasPreviousPage once backend fixes naming
          false
        ),
        hasNextPage: _.get(
          afterCursorData,
          "node.messagesV2.pageInfo.hasNextPage",
          false
        ),
        startCursor: _.get(
          beforeCursorData,
          "node.messagesV2.pageInfo.endCursor", // TODO: change to startCursor once backend fixes naming
          null
        ),
        endCursor: afterCursorData
          ? _.get(afterCursorData, "node.messagesV2.pageInfo.endCursor", null)
          : _.get(lastReadMessageEdge, "node.cursor", null),
        __typename: "PageInfo",
      },
    },
  };
};

const MAX_MESSAGES_LIMIT_IN_CACHE = 50;

// instead of always trimming the data to MAX_MESSAGES_LIMIT_IN_CACHE
// especially when received new messages, add some buffer to it(for comparison only)
const REALTIME_BUFFER = 10;

// do not trim the data if we are jumping to message present in cache
const getCachedDataQueryState = ({ messagesList, isJumpToMessage }) =>
  _.size(messagesList) > MAX_MESSAGES_LIMIT_IN_CACHE + REALTIME_BUFFER &&
  !isJumpToMessage
    ? { isLoading: true, isData: false, isMessagesSizeLimitReached: true }
    : { isLoading: false, isData: true };

/*
    We only need to fetch data around the cursor if:
    1. When messagesList is empty
    2. If We have data in cache, but we don't have data around the cursor,
      then fetch only if we have unread messages or jump to message.
      Else use data from cache with preserved scroll position or from the bottom
  */
export const getQueryState = ({
  chatId,
  cursorState,
  unreadMessageCount,
  organizationId = getOrganizationId(),
  cachedData = getChatConversationsFromCache({
    id: chatId,
    organizationId,
  }),
}) => {
  const messageId = _.get(cursorState, "cursorMessageNode.id", null);
  const cursorMessageType = _.get(
    cursorState,
    "cursorMessageType",
    CURSOR_MESSAGE_TYPES.LAST_READ_MESSAGE
  );

  // if we have empty messages, fetch data every time user opens chat
  const messagesList = _.get(cachedData, "messagesV2.edges", []);
  if (_.isEmpty(messagesList)) {
    return { isLoading: true, isData: false };
  }

  const sessionKey = getChatSessionKey({ chatId, organizationId });
  // this would help in fetching data around cursor always for first time
  // ignoring the cache-persistence. sessionKey is set in getConversations
  if (!SESSION_SET.has(sessionKey)) {
    return { isLoading: true, isData: false };
  }

  // if we have unread messages or jump to message,
  // fetch data around the cursor, only if not present in cache
  const isJumpToMessage =
    cursorMessageType === CURSOR_MESSAGE_TYPES.JUMP_TO_MESSAGE;
  if (unreadMessageCount || isJumpToMessage) {
    const cursorMessageInList = _.find(messagesList, edge =>
      isIdEqual(_.get(edge, "node.id"), messageId)
    );

    return _.isEmpty(cursorMessageInList)
      ? { isLoading: true, isData: false }
      : getCachedDataQueryState({ messagesList, isJumpToMessage });
  }

  // is no unread messages or no jump to message, use data from cache
  // with preserved scroll position or from the bottom
  return getCachedDataQueryState({ messagesList, isJumpToMessage: false });
};

export const trimCachedMessagesData = ({
  chatId,
  messageId, // message id around which we need to trim the data
}) => {
  const organizationId = getOrganizationId();
  const cachedData = getChatConversationsFromCache({
    id: chatId,
    organizationId,
  });
  const edges = _.get(cachedData, "messagesV2.edges", []);

  if (_.size(edges) <= MAX_MESSAGES_LIMIT_IN_CACHE) return;

  const oldHasPreviousPage = _.get(
    cachedData,
    "messagesV2.pageInfo.hasPreviousPage",
    false
  );
  const oldHasNextPage = _.get(
    cachedData,
    "messagesV2.pageInfo.hasNextPage",
    false
  );

  const messageIdx = messageId
    ? _.findIndex(edges, edge => isIdEqual(_.get(edge, "node.id"), messageId))
    : -1;
  const isMessagePresent = messageIdx !== -1;

  // trim the edges from both sides around the messageIdx
  // to get MAX_MESSAGES_LIMIT_IN_CACHE messages
  const start = isMessagePresent
    ? Math.max(messageIdx - MAX_MESSAGES_LIMIT_IN_CACHE, 0)
    : 0;

  const end = isMessagePresent
    ? Math.min(messageIdx + MAX_MESSAGES_LIMIT_IN_CACHE, _.size(edges))
    : MAX_MESSAGES_LIMIT_IN_CACHE;

  const updatedEdges = _.slice(edges, start, end);

  // edges are is desc order, so startCursor will be last non-local edge's cursor
  // and vice-versa and similarly for hasPreviousPage and hasNextPage
  const startCursor = _.get(
    _.findLast(
      updatedEdges,
      edge => !getIsLocalMessage(_.get(edge, "node.id"))
    ),
    "node.cursor",
    null
  );

  const endCursor = _.get(
    _.find(updatedEdges, edge => !getIsLocalMessage(_.get(edge, "node.id"))),
    "node.cursor",
    null
  );

  // if all the trimmed edges are local messages,
  // do nothing, as its extremely rare case(next to impossible)
  if (!startCursor || !endCursor) return;

  const updatedPageInfo = {
    ..._.get(cachedData, "messagesV2.pageInfo"),
    startCursor,
    endCursor,
    hasPreviousPage: end < _.size(edges) ? true : oldHasPreviousPage,
    hasNextPage: start > 0 ? true : oldHasNextPage,
  };

  const payload = update(cachedData, {
    messagesV2: {
      edges: { $set: updatedEdges },
      pageInfo: { $set: updatedPageInfo },
    },
  });

  writeChatConversationsToCache({
    variables: { id: chatId, organizationId },
    chatConversationsData: payload,
  });
};

const DIRECTION = {
  NEXT: "NEXT",
  PREVIOUS: "PREVIOUS",
};

const loadMoreData = async ({
  chatId,
  direction,
  pageSize = PAGINATION_LIMITS.FETCH_MORE_MESSAGES,
}) => {
  const organizationId = getOrganizationId();
  let cachedData = getChatConversationsFromCache({
    id: chatId,
    organizationId,
  });

  const hasMoreKey =
    direction === DIRECTION.NEXT ? "hasNextPage" : "hasPreviousPage";
  const cursorKey = direction === DIRECTION.NEXT ? "endCursor" : "startCursor";
  const cursor = _.get(cachedData, ["messagesV2", "pageInfo", cursorKey]);

  // In case of fetching next messages, cursor should never be null.
  // If it is null, it means we have reached the end of the list
  // So this function should never be called in that case
  if (direction === DIRECTION.NEXT && !cursor) return;

  const input = {
    first: pageSize,
    excludeOtherUserMessageType: [MESSAGE_TYPE.CONVERSATION_SUMMARY],
  };

  // TODO: reverse before/after once backend fixes
  if (direction === DIRECTION.NEXT) input.before = cursor;
  else input.after = cursor;

  try {
    const response = await client.query({
      query: getChatConversationsQuery,
      fetchPolicy: "no-cache",
      variables: { id: chatId, organizationId, input },
    });
    const responseMessagesV2 = _.get(response, "data.node.messagesV2");

    // would never happen though
    if (!responseMessagesV2) return;

    // read latest data from cache
    cachedData = getChatConversationsFromCache({ id: chatId, organizationId });
    const cachedEdges = _.get(cachedData, "messagesV2.edges", []);

    // they are merged in descending order
    const mergedEdges =
      direction === DIRECTION.NEXT
        ? [..._.reverse([...responseMessagesV2.edges]), ...cachedEdges]
        : [...cachedEdges, ...responseMessagesV2.edges];

    const newState = {
      ...cachedData,
      messagesV2: {
        ..._.get(cachedData, "messagesV2"),
        edges: _.uniqBy(mergedEdges, "node.id"),
        pageInfo: {
          ..._.get(cachedData, "messagesV2.pageInfo"),
          // TODO: use correct hasMore and cursor(names) once backend fixes
          [cursorKey]: _.get(responseMessagesV2, "pageInfo.endCursor"),
          [hasMoreKey]: _.get(responseMessagesV2, "pageInfo.hasNextPage"),
        },
      },
    };

    writeChatConversationsToCache({
      variables: { id: chatId, organizationId },
      chatConversationsData: newState,
    });
    return response;
  } catch (err) {
    return undefined;
  }
};

const STABLE_EMPTY_OBJ = {};

const useMessagesData = props => {
  const { chatId } = props;

  const dispatch = useDispatch();
  const [, rerender] = useReducer(s => s + 1, 0);
  const organizationId = getOrganizationId();
  const observableQuery = useMemo(() => {
    return client.watchQuery({
      query: getChatConversationsQuery,
      fetchPolicy: "cache-only",
      variables: { id: chatId, organizationId },
      returnPartialData: false,
    });
  }, [chatId, organizationId]);

  const getConversations = useCallback(
    async ({ chatId, cursorMessageNode, cleanUpRef }) => {
      const cursor = _.get(cursorMessageNode, "cursor", null);
      const cursorMessageId = _.get(cursorMessageNode, "id", null);
      const sessionKey = getChatSessionKey({
        chatId,
        organizationId,
      });
      if (!SESSION_SET.has(sessionKey)) SESSION_SET.add(sessionKey);

      try {
        const promises = [];

        // before cursor query
        promises.push(
          client.query({
            query: getChatConversationsQuery,
            fetchPolicy: "no-cache",
            variables: {
              id: chatId,
              organizationId,
              // TODO: change after to before once backend fixes
              // (As of now they reversed the naming of cursors)
              input: {
                first: PAGINATION_LIMITS.MESSAGES_FEED,
                after: cursor,
                excludeOtherUserMessageType: [
                  MESSAGE_TYPE.CONVERSATION_SUMMARY,
                ],
              },
            },
          })
        );

        if (cursor && cursorMessageId) {
          // after cursor query
          promises.push(
            client.query({
              query: getChatConversationsQuery,
              fetchPolicy: "no-cache",
              variables: {
                id: chatId,
                organizationId,
                // TODO: change before to after once backend fixes
                // (As of now they reversed the naming of cursors)
                input: {
                  first: PAGINATION_LIMITS.MESSAGES_FEED,
                  before: cursor,
                  excludeOtherUserMessageType: [
                    MESSAGE_TYPE.CONVERSATION_SUMMARY,
                  ],
                },
              },
            })
          );

          // cursor message query
          promises.push(
            client.query({
              query: getMessageQuery,
              fetchPolicy: "network-only",
              variables: { id: cursorMessageId, organizationId },
            })
          );
        }

        const [
          beforeCursorData,
          afterCursorData,
          cursorMessageData,
        ] = await Promise.all(promises);

        const payload = preparePayload({
          beforeCursorData: beforeCursorData.data,
          // following can be undefined in case of null cursor
          afterCursorData: _.get(afterCursorData, "data"),
          cursorMessageData: _.get(cursorMessageData, "data"),
        });
        if (cleanUpRef.isCleanedUp) return true;
        writeChatConversationsToCache({
          variables: { id: chatId, organizationId },
          chatConversationsData: payload,
        });
        return true;
      } catch (err) {
        dispatch(setToastMsg("toastMsgs:chat_messages_query_error"));
        return false;
      }
    },
    [dispatch, organizationId]
  );

  useEffect(() => {
    // Subscribe to cache updates of cache-only custom combined query
    const subscription = observableQuery.subscribe(() => {
      rerender();
    });
    return () => subscription.unsubscribe();
  }, [observableQuery]);

  // to cleanup failed messages from persisted cache
  // this function remove all the message edges whose id will start with "NEW_"
  // and don't have corresponding status in redux store
  useEffect(() => {
    dispatch(cleanUpFailedMessages({ chatId }));
  }, [dispatch, chatId]);

  const loadNextMoreMessages = useCallback(
    async pageSize => {
      const response = await loadMoreData({
        chatId,
        direction: DIRECTION.NEXT,
        pageSize,
      });
      return response;
    },
    [chatId]
  );

  const loadPreviousMoreMessages = useCallback(
    async pageSize => {
      const response = await loadMoreData({
        chatId,
        direction: DIRECTION.PREVIOUS,
        pageSize,
      });
      return response;
    },
    [chatId]
  );

  // Better way to get data from cache than readQuery
  // Ref: https://github.com/trojanowski/react-apollo-hooks/blob/ef67f732b639cb359cb31838ade5e4b513019458/src/useQuery.ts#L143
  const cachedData = _.get(
    observableQuery.getCurrentResult(),
    "data.node",
    STABLE_EMPTY_OBJ
  );

  const conversations = isMessagingRevempEnabled()
    ? _.filter(
        _.get(cachedData, "messagesV2.edges", []),
        ({ node }) => !node.isDeleted
      )
    : _.get(cachedData, "messagesV2.edges", []);

  return {
    conversations,
    hasNextPage: _.get(cachedData, "messagesV2.pageInfo.hasNextPage", false),
    hasPreviousPage: _.get(
      cachedData,
      "messagesV2.pageInfo.hasPreviousPage",
      false
    ),
    getConversations,
    loadNextMoreMessages,
    loadPreviousMoreMessages,
  };
};

export default useMessagesData;
